import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const GPSCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processGPSCallback = () => {
      try {
        // Get token from URL parameters (GPS Web callback format)
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
          if (error === 'access_denied') {
            setError('Authentication was cancelled. Please try again.');
          } else {
            setError(`GPS Web authentication failed: ${errorDescription || error}`);
          }
          setIsProcessing(false);
          return;
        }

        if (token) {
          console.log('📡 GPS Web token received:', token.substring(0, 20) + '...');
          
          // Store the GPS Web token using the same keys as Azure AD callback
          localStorage.setItem('chime_token', token);
          
          // Set token expiry (1 hour from now)
          const expiryTime = Date.now() + (60 * 60 * 1000);
          localStorage.setItem('chime_token_expires_at', expiryTime.toString());
          
          console.log('✅ GPS Web authentication successful');
          
          // Clear session storage auth attempt flag
          sessionStorage.removeItem('auth_attempt');
          
          // Redirect to main app
          window.location.href = '/';
        } else {
          setError('No authentication token received from GPS Web.');
          setIsProcessing(false);
        }
      } catch (err) {
        console.error('Error processing GPS Web authentication callback:', err);
        setError('Failed to process GPS Web authentication response.');
        setIsProcessing(false);
      }
    };

    processGPSCallback();
  }, [navigate]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing GPS Web Authentication...</h2>
          <p className="text-gray-600">Please wait while we complete your login.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">GPS Web Authentication Error</h3>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GPSCallback;